import React, { useEffect, useMemo, useRef } from "react";
import { Col } from "reactstrap";
import { Space } from "antd";
import { Button } from "react-bootstrap";
import { PageHeader } from "../../CommonElements/PageTitle";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  useCreateArrivalSegmentMutation,
  useGetOneArrivalQuery,
  useUpdateArrivalSegmentMutation,
} from "../../services/arrivals";
import { InitialCampaignForm } from "../BasicData/InitialCampaign/InitialCampaignForm";
import { useForm, FormProvider } from "react-hook-form";
import { PickKeyPairs } from "@/utils/helpers";
import TableC from "@/CommonElements/TableC";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { HugeiconsIcon } from "@hugeicons/react";
import { Edit02Icon } from "@hugeicons/core-free-icons/index";
import { Row, Stack } from "react-bootstrap";
import { Divider } from "antd";
import { useDeleteArrivalSegmentMutation } from "@/services/arrivals";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { AddressCapacityDisplay } from "@/CommonElements/AddressCapacityDisplay";
import { AddressDate } from "@/Component/contracts/AddressDate";
import BtnAsLink from "@/Component/BtnAsLink";
import { useCampaignDetailsForm } from "./hooks/useCampaignDetailsForm";
import { CampaignDetailsForm } from ".";
import { useCampaignDetailsTable } from "./hooks/useCampaignDetailsTable";
import { useSystem } from "@/utils/hooks/useLocation";
import { MAKKAH_PERMISSIONS } from "@/utils/constants";
import { generatePermissionName } from "@/utils/permissions";
import { Can } from "@/CommonElements/PermissionGate";
import { NUSUK_CITY_ID } from "@/utils/constants";

const UpdateCampaign = () => {
  const { currentSystem, from_id } = useSystem();
  // =======
  // Hooks
  // =======
  const { open, handleClose, handleOpen, selectedSegmentId } =
    useCampaignDetailsForm();
  const form = useForm();
  const { t } = useTranslation();
  const { id, port, mode } = useParams();
  const canAmend = mode === "edit" || mode === "create";
  const { getActions, columns } = useCampaignDetailsTable(true);
  const { cachedBasicData } = useCachedBasicData();
  const { confirm, ModalComponent } = useConfirm();


  // ==================
  // Queries & Mutations
  // ==================
  const { mutate: deleteSegment } = useDeleteArrivalSegmentMutation();
  const {
    data: arrivalRes,
    isLoading: arrivalLoading,
    isFetching: arrivalFetching,
  } = useGetOneArrivalQuery({ id, port });
  const arrivalData = arrivalRes?.data.data ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const segments = arrivalData?.segments || [];
  const loading = arrivalLoading || arrivalFetching;
  const totalCapacity = segments.reduce(
    (acc, segment) => acc + segment.capacity,
    0
  );

  const emptySegment = useRef({}).current;
  const selectedSegment = useMemo(
    () => segments?.find((segment) => segment.id === selectedSegmentId) ?? emptySegment,
    [segments, selectedSegmentId, emptySegment]
  );

  // =======
  // Mutations
  // =======
  const { mutate: createSegment, isPending: isCreating } =
    useCreateArrivalSegmentMutation();
  const { mutate: updateSegment, isPending: isUpdating } =
    useUpdateArrivalSegmentMutation();
  const isPending = isCreating || isUpdating;
  useEffect(() => {
    const initialData = PickKeyPairs(arrivalData, [
      "campaign_name",
      "campaign_number",
      "country_id",
      "nationality_id",
      "service_center_id",
      "madina_service_center_id",
      "state_id",
      "supervisor_mobile_number",
      "supervisor_name",
      "visit",
    ])

    form.reset(initialData);
  }, [arrivalData]);

  const handleDelete = (id) => {
    confirm({
      title: t("CONFIRM"),
      content: (
        <Trans
          i18nKey="CONFIRM_DELETE_FLIGHT"
          components={{ danger: <span className="text-danger" /> }}
        />
      ),
      onOk: () => {
        deleteSegment({ segmentId: id });
      },
      onCancel: () => { },
    });
  };

  const selectedContracts = useMemo(() => {
    return (
      cachedBasicData?.contracts?.filter((contract) =>
        segments.some(
          (segment) => String(segment.contract_id) === String(contract.id)
        )
      ) || []
    );
  }, [segments, cachedBasicData]);

  const selectedContractsMakkah = useMemo(
    () => selectedContracts.filter((c) => String(c?.city_id) === String(NUSUK_CITY_ID.makkah)),
    [selectedContracts]
  );
  const selectedContractsMadina = useMemo(
    () => selectedContracts.filter((c) => String(c?.city_id) === String(NUSUK_CITY_ID.madina)),
    [selectedContracts]
  );


  return (
    <div>
      <PageHeader title={t(canAmend ? "UPDATE_CAMPAIGN" : "CAMPAIGN_DETAILS")}>
        {!canAmend && (
          <Stack direction="horizontal" gap={2}>
            <Can permission={generatePermissionName(from_id, MAKKAH_PERMISSIONS.update_campaigns)}>
              <BtnAsLink
                icon={<HugeiconsIcon size={18} icon={Edit02Icon} />}
                className="btn btn-primary"
                link={`/app/${currentSystem}/basic-data/campaigns/${id}/edit`}
              >
                <span>{t("EDIT_CAMPAING")}</span>
              </BtnAsLink>
            </Can>
            <Can permission={generatePermissionName(from_id, MAKKAH_PERMISSIONS.create_campaign_details)}>
              <BtnAsLink
                icon={<HugeiconsIcon size={18} icon={Edit02Icon} />}
                className="btn btn-outline-primary"
                link={`/app/${currentSystem}/arrival-campaign/${id}/${port}/edit`}
              >
                <span>{t("ADD_DETAILS")}</span>
              </BtnAsLink>
            </Can >
          </Stack>
        )}
      </PageHeader>

      <div className="m-4">
        <FormProvider {...form}>
          <InitialCampaignForm isLoading={loading} isViewMode={true} />

          <Row className="mb-3">
            <AddressCapacityDisplay
              items={selectedContractsMakkah}
              madinahItems={selectedContractsMadina}
              isLoading={loading}
              totalCapacity={totalCapacity}
              capacityLabel={t("TOTAL_PILGRIMS")}
              addressLabel={t("HOUSING_CONTRACTS_IN_MAKKAH")}
              madinahAddressLabel={t("HOUSING_CONTRACTS_IN_MADINAH")}
              pilgrimCount={arrivalData?.pilgrim_count ?? 0}
              pilgrimCountLabel={t("NUMBER_OF_ACCOMMODATED")}
              emptyMessage={t("NO_ADDRESSES_FOUND")}
              primaryItemFormatter={(contract) => {
                const { address, id } = contract;
                return [id, address].join(" - ");
              }}
              renderItem={(contract) => (
                <AddressDate contract={contract} key={contract?.id} />
              )}
            />
          </Row>
          <Divider />
          <TableC
            data={arrivalData?.segments ?? []}
            columns={columns}
            actions={getActions({
              edit: (rowData) => ({
                onClick: () => handleOpen(rowData.id),
              }),
              delete: (rowData) => ({
                onClick: () => handleDelete(rowData.id),
              }),
            })}
            loading={loading}
            columnsFilter={false}
          />

          <CampaignDetailsForm
            mutation={{
              create: createSegment,
              update: updateSegment,
              isPending: isPending,
            }}
            totalCapacity={totalCapacity}
            // hook values
            isOpen={open}
            onClose={handleClose}
            selectedSegment={selectedSegment}
            isLoading={loading}
          />
        </FormProvider>
        {canAmend && (
          <Col
            md={12}
            className="d-flex justify-content-center align-items-center"
          >
            <Button className="btn-text" onClick={() => handleOpen()}>
              <Space>
                <i className="icon-plus"></i>
                <span>{t("CAMPAIGNS_FORM_ADD_SEGMENT_DETAILS")}</span>
              </Space>
            </Button>
          </Col>
        )}
      </div>

      {<ModalComponent />}
    </div>
  );
};

export default UpdateCampaign;
